import { useMemo, useState } from 'react';
import {
  KeyIcon,
  LockIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FieldError } from '@/components/ui/field-error';
import { ManageGroupsContent } from '@/content/ManageGroupsContent';
import type { ManageGroup, GroupFormValues } from '@/lib/types/groups.types';
import type { UserRole } from '@/lib/types/database';
import {
  generateGroupImportKeyPreview,
  formatGroupCreatedContext,
  getGroupRoleTone,
  isUserRole,
} from '@/lib/utils/groups.utils';
import { getOrderedRoleKeys } from '@/lib/utils/roles.utils';
import { cn } from '@/lib/utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';

export type GroupFormSubmitInput = {
  displayName: string;
  parentRole: UserRole;
  description: string | null;
};

export interface GroupFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: ManageGroup | null;
  isSubmitting: boolean;
  onSubmit: (input: GroupFormSubmitInput) => Promise<void>;
  onRequestArchive?: () => void;
  onRequestRestore?: () => void;
}

const initialValues: GroupFormValues = {
  displayName: '',
  parentRole: '',
  description: '',
};

export function GroupFormDrawer({
  open,
  onOpenChange,
  group,
  isSubmitting,
  onSubmit,
  onRequestArchive,
  onRequestRestore,
}: GroupFormDrawerProps) {
  const resetKey = `${String(open)}-${group?.id ?? 'new'}`;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <GroupFormContent
        key={resetKey}
        open={open}
        onOpenChange={onOpenChange}
        group={group}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        onRequestArchive={onRequestArchive}
        onRequestRestore={onRequestRestore}
      />
    </Drawer>
  );
}

function GroupFormContent({
  group,
  isSubmitting,
  onSubmit,
  onOpenChange,
  onRequestArchive,
  onRequestRestore,
}: GroupFormDrawerProps) {
  const [values, setValues] = useState<GroupFormValues>(
    group
      ? {
          displayName: group.display_name,
          parentRole: group.parent_role,
          description: group.description ?? '',
        }
      : initialValues
  );
  const [submitted, setSubmitted] = useState(false);

  const isEditing = !!group;
  const content = ManageGroupsContent.drawer;

  const importKeyPreview = useMemo(
    () => generateGroupImportKeyPreview(values.displayName),
    [values.displayName]
  );

  const displayNameError =
    submitted && !values.displayName.trim()
      ? content.validation.missingName
      : null;
  const parentRoleError =
    submitted && !values.parentRole
      ? content.validation.missingParentRole
      : null;
  const importKeyError =
    submitted && values.displayName.trim() && !importKeyPreview && !isEditing
      ? content.validation.emptyImportKey
      : null;
  const showParentRoleWarning =
    !!group && group.parent_role !== values.parentRole;

  const handleSubmit = async () => {
    setSubmitted(true);

    if (
      !values.displayName.trim() ||
      !isUserRole(values.parentRole) ||
      (!isEditing && !importKeyPreview)
    ) {
      return;
    }

    await onSubmit({
      displayName: values.displayName,
      parentRole: values.parentRole,
      description: values.description.trim() || null,
    });
  };

  return (
    <DrawerContent
      aria-label={isEditing ? content.editTitle : content.createTitle}
      className="max-h-[92vh] rounded-t-[20px] bg-slate-700 px-4 pb-6 pt-4 sm:left-1/2 sm:max-w-lg sm:-translate-x-1/2 sm:px-5"
    >
      <div className="mx-auto flex max-h-[calc(92vh-3rem)] w-full max-w-lg flex-col">
        <DrawerHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DrawerTitle className="font-field-64 text-[22px] leading-7 text-slate-50">
                {isEditing ? content.editTitle : content.createTitle}
              </DrawerTitle>
              <p className="mt-1 text-small text-slate-400">
                {isEditing && group
                  ? formatGroupCreatedContext(
                      group.created_at,
                      group.member_count
                    )
                  : content.createDescription}
              </p>
            </div>
            <DrawerClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mr-2 -mt-1 h-9 w-9 text-slate-400 hover:bg-slate-600 hover:text-slate-50"
                aria-label={ManageGroupsContent.actions.close}
                disabled={isSubmitting}
              >
                <XIcon size={18} weight="bold" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-2 pb-4">
          <div className="space-y-2">
            <Label
              className="font-field-42 text-[11px] uppercase text-slate-400"
              htmlFor="group-name"
            >
              {content.fields.displayName}
              <span className="text-red-400"> *</span>
            </Label>
            <Input
              id="group-name"
              value={values.displayName}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  displayName: event.target.value,
                }))
              }
              placeholder={content.fields.displayNamePlaceholder}
              aria-invalid={!!displayNameError}
              disabled={isSubmitting}
              className={cn(
                'h-12 rounded-lg border-slate-550 bg-slate-600 text-slate-50 placeholder:text-slate-500 focus-visible:border-purple-300 focus-visible:ring-purple-300',
                displayNameError && 'border-red-500'
              )}
            />
            {displayNameError && <FieldError errors={[displayNameError]} />}
          </div>

          <div
            className={cn(
              'rounded-lg p-4',
              isEditing
                ? 'border border-dashed border-slate-500 bg-slate-700/70'
                : 'border border-slate-550 bg-slate-600'
            )}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 font-field-42 text-[11px] uppercase text-slate-400">
                <KeyIcon size={14} weight="bold" />
                {isEditing
                  ? content.fields.importKey
                  : content.fields.importKeyPreview}
              </span>
              {isEditing && (
                <span className="flex items-center gap-1 text-small text-slate-500">
                  <LockIcon size={12} weight="bold" />
                  Locked
                </span>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              <code
                className={cn(
                  'min-h-6 min-w-0 break-all font-mono text-body text-purple-300'
                )}
              >
                {isEditing
                  ? group?.import_key
                  : importKeyPreview || content.fields.emptyImportKey}
              </code>
            </div>
            <p className="mt-2 flex gap-2 text-small text-slate-400 items-center">
              <LockIcon size={12} weight="bold" className="mt-0.5 shrink-0" />
              <span>
                {isEditing
                  ? content.lockedImportKeyHelp
                  : content.importKeyHelp}
              </span>
            </p>
            {importKeyError && (
              <div className="mt-2">
                <FieldError errors={[importKeyError]} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="font-field-42 text-[11px] uppercase text-slate-400">
              {content.fields.parentRole}
              <span className="text-red-400"> *</span>
            </Label>
            <div
              className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2"
              role="radiogroup"
              aria-label={content.fields.parentRole}
              aria-invalid={!!parentRoleError}
            >
              {getOrderedRoleKeys().map((role) => (
                <ParentRoleTile
                  key={role}
                  role={role}
                  selected={values.parentRole === role}
                  disabled={isSubmitting}
                  onSelect={() =>
                    setValues((current) => ({
                      ...current,
                      parentRole: role,
                    }))
                  }
                />
              ))}
            </div>
            <p className="text-small text-slate-500">
              {content.parentRoleHelp}
            </p>
            {parentRoleError && <FieldError errors={[parentRoleError]} />}
          </div>

          {showParentRoleWarning && (
            <div className="flex gap-3 rounded-lg border border-yellow-300/40 bg-yellow-300/10 p-3 text-body text-yellow-100">
              <WarningCircleIcon
                size={20}
                weight="bold"
                className="mt-0.5 flex-shrink-0 text-yellow-300"
              />
              <p>{content.parentRoleWarning}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label
              className="font-field-42 text-[11px] uppercase text-slate-400"
              htmlFor="group-description"
            >
              {content.fields.description}
              <span className="text-slate-500"> · optional</span>
            </Label>
            <Textarea
              id="group-description"
              value={values.description}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              placeholder={content.fields.descriptionPlaceholder}
              disabled={isSubmitting}
              className="min-h-20 rounded-lg border-slate-550 bg-slate-600 text-slate-50 placeholder:text-slate-500 focus-visible:border-purple-300 focus-visible:ring-purple-300"
            />
          </div>
        </div>

        <DrawerFooter className="flex-col gap-2 px-2 pt-2">
          <div className="flex gap-2">
            <DrawerClose asChild>
              <Button
                variant="outline"
                size="lg"
                disabled={isSubmitting}
                className="h-12 flex-1 border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
              >
                {ManageGroupsContent.actions.cancel}
              </Button>
            </DrawerClose>
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="h-12 flex-1 bg-purple-300 text-slate-700 hover:bg-purple-400"
            >
              {isSubmitting
                ? isEditing
                  ? content.saving
                  : content.creating
                : isEditing
                  ? content.editConfirm
                  : content.createConfirm}
            </Button>
          </div>
          {isEditing && (onRequestArchive || onRequestRestore) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isSubmitting}
              onClick={() => {
                onOpenChange?.(false);
                setTimeout(() => {
                  if (group?.archived_at) {
                    onRequestRestore?.();
                  } else {
                    onRequestArchive?.();
                  }
                }, 150);
              }}
              className={cn(
                'h-9 w-full text-small',
                group?.archived_at
                  ? 'text-slate-400 hover:bg-slate-600 hover:text-slate-50'
                  : 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
              )}
            >
              {group?.archived_at
                ? ManageGroupsContent.actions.restore
                : ManageGroupsContent.actions.archive}
            </Button>
          )}
        </DrawerFooter>
      </div>
    </DrawerContent>
  );
}

interface ParentRoleTileProps {
  role: UserRole;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function ParentRoleTile({
  role,
  selected,
  disabled,
  onSelect,
}: ParentRoleTileProps) {
  const tone = getGroupRoleTone(role);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex min-h-11 items-center gap-2 rounded-lg border border-slate-550 bg-slate-600 px-3 py-2 text-left text-body text-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 disabled:cursor-not-allowed disabled:opacity-50',
        selected && tone.radioClassName
      )}
    >
      <span
        className={cn(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-slate-400',
          selected && 'border-current'
        )}
      >
        {selected && (
          <span
            className={cn('h-2 w-2 rounded-full', tone.radioDotClassName)}
          />
        )}
      </span>
      <span className="min-w-0 truncate">{getRoleLabelByKey(role)}</span>
    </button>
  );
}
